import { create } from 'zustand';
import { useSearch } from '@tanstack/react-router';
import type { AdminRange } from './types';

/**
 * The admin window lives in the URL (`?range=7d`, or `?range=custom&start=…&end=…`), so every view
 * is bookmarkable and survives a reload; links between admin pages carry it along (`search: true`).
 */
export interface AdminSearch {
  range?: AdminRange;
  start?: string;
  end?: string;
}

const RANGES: readonly AdminRange[] = ['24h', '7d', '30d', '90d', 'custom'];
const isDate = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

export function validateAdminSearch(s: Record<string, unknown>): AdminSearch {
  const range = RANGES.includes(s.range as AdminRange) ? (s.range as AdminRange) : undefined;
  return {
    range,
    start: range === 'custom' && isDate(s.start) ? s.start : undefined,
    end: range === 'custom' && isDate(s.end) ? s.end : undefined,
  };
}

/** The window query every page request carries. A custom range without both dates falls back to 30d. */
export function useAdminWindow(): { range: AdminRange; start?: string; end?: string } {
  const s = useSearch({ strict: false }) as AdminSearch;
  if (s.range === 'custom') {
    if (s.start && s.end) return { range: 'custom', start: s.start, end: s.end };
    return { range: '30d' };
  }
  return { range: s.range ?? '30d' };
}

/**
 * The refresh button: bumps a stamp that is part of every admin query key, and the next fetch asks
 * the server to bypass its cache (`fresh=true`) for a few seconds after the click.
 */
interface RefreshState {
  stamp: number;
  refresh: () => void;
}

export const useAdminRefresh = create<RefreshState>((set) => ({
  stamp: 0,
  refresh: () => set({ stamp: Date.now() }),
}));

export const isFresh = (stamp: number) => stamp > 0 && Date.now() - stamp < 5000;

/** The latest page's `generated_at`, for "Updated 09:41" in the header. `Loaded` publishes it. */
export const useAdminStamp = create<{ at: string | null; set: (at: string | null) => void }>((set) => ({
  at: null,
  set: (at) => set({ at }),
}));
